import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import color from 'tinycolor2';
import { Draggable, Droppable } from 'react-beautiful-dnd';
import { 
  PARENT,
  CHILD,
  FIRST_CHILD,
} from 'constants/knowledgeMap';
import compareDocumentItems from './compareDocumentItems';
import KnowledgeMapNode from '../KnowledgeMapNode';

const getBackgroundColor = (props) =>
  color.mix(props.theme['color.primary'], props.theme['color.background'], 60).toString();

const ChildItemsContainer = styled.div`
  margin-left: 30px;
`;

const ChildLongTrail = styled.div`
  position: relative;

  &::before {
    ${({ isLast }) => (isLast ? '' : 'content: \'\';')}
    position: absolute;
    left: -15px;
    top: 0;
    bottom: 0;
    width: 2px;
    background-color: ${getBackgroundColor};
  }
`;

const EmptyChildTrail = styled.div`
  position: relative;

  &::before {
    content: '';
    position: relative;
    left: -15px;
    top: 0;
    bottom: 0;
    width: 2px;
    background-color: ${getBackgroundColor};
  }
`;

const TrailCorner = styled.div`
  position: absolute;
  width: 15px;
  height: 17px;
  top: 10px;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    border: 2px solid ${getBackgroundColor};
    border-radius: 50%;
    width: 30px;
    height: 30px;
    top: -14px;
  }
`;

const TrailWrapper = styled.div`
  position: absolute;
  left: -15px;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    width: 2px;
    top: -5px;
    height: 15px;
    background-color: ${getBackgroundColor};
  }
`;

// eslint-disable-next-line react/prop-types
function ExpandableTreeItem(props) {
  const { 
    item,
    onSelect,
    competencyDocumentParentItems,
    competencyTree,
    expandedById,
    isParentDragDisabled,
    isChildDragDisabled,
    render,
    depth,
    addNodeClickHandler, 
  } = props;
  const hasChildren = competencyTree.isCompetencyDocument(item.id) || competencyTree.hasChildren(item.id);
  const childItems = competencyDocumentParentItems || (hasChildren && competencyTree.getChildren(item.id));
  const isExpanded = expandedById[item.id];
  const isDropDisabled = depth === 1?isParentDragDisabled : isChildDragDisabled;
  const dropType = depth === 1 ? PARENT : CHILD;
  const isParent = competencyTree.getAllParentsByDeepChildId(item.id).length;
  const hasDummyChild = isExpanded && !hasChildren && !isParent;
  
  return (
    <React.Fragment>
      <TrailWrapper>
        <TrailCorner />
      </TrailWrapper>
      {render({ item, depth, hasDummyChild })}
      <ChildItemsContainer>
        { isExpanded && hasChildren && childItems.sort(compareDocumentItems).map((childItem, index) => (
          <ChildLongTrail
            key={childItem.id}
            isLast={index === (childItems.length - 1)}
          >
            <Droppable droppableId={childItem.titleKey} isDropDisabled={isDropDisabled} type={dropType} index={childItem.id}>
              {(provided) => (
                <div ref={provided.innerRef} >
                  <Draggable draggableId={childItem.titleKey} isDragDisabled={isDropDisabled} index={childItem.id}>
                    {(provided) => (
                      <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                        <ExpandableTreeItem
                          item={childItem}
                          onSelect={onSelect}
                          competencyTree={competencyTree}
                          expandedById={expandedById}
                          isParentDragDisabled={isParentDragDisabled}
                          isChildDragDisabled={isChildDragDisabled}
                          addNodeClickHandler={addNodeClickHandler}
                          render={render}
                          depth={depth + 1}
                        />
                      </div>
                    )}
                  </Draggable>
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </ChildLongTrail>
        ))}
        { isExpanded && !hasChildren && !isParent &&
          <EmptyChildTrail >
            <Droppable droppableId={`${item.titleKey}_${FIRST_CHILD}-${item.id}`} isDropDisabled={isDropDisabled} type={dropType} index={item.id}>
              {(provided) => (
                <div ref={provided.innerRef} >
                  <TrailWrapper>
                    <TrailCorner />
                  </TrailWrapper>
                  <KnowledgeMapNode
                    addNodeClickHandler={(event) => addNodeClickHandler(item, event)}
                    isEmptyChildItem={true}
                  />
                </div>
              )}
            </Droppable>
          </EmptyChildTrail>
        }
      </ChildItemsContainer>
    </React.Fragment>
  );
}

ExpandableTreeItem.propTypes = {
  item: PropTypes.object.isRequired,
  competencyTree: PropTypes.object.isRequired,
  expandedById: PropTypes.object.isRequired,
  competencyDocumentParentItems: PropTypes.array,
  isParentDragDisabled: PropTypes.bool,
  isChildDragDisabled: PropTypes.bool,
  render: PropTypes.func,
  depth: PropTypes.number,
  duplicateKnowledgeMap: PropTypes.func,
};

ExpandableTreeItem.defaultProps = {
  depth: 1,
};

export default ExpandableTreeItem;
